import { useMemo, useEffect, useState, useRef } from 'preact/hooks'

import { AthosSearchProvider } from '../fetchers/search'
import { BeaconAnalyticsProvider, useBeaconLogImpressions, useBeaconLogRender, useBeaconLogRedirect } from '../hooks/useBeaconAnalytics'
import { useOption } from '../hooks/useOption'
import { useAthosContext } from '../fetchers/provider'
import { useMerchedResults } from '../hooks/useMerchedResults'

import { scrollHistoryManager } from '@/components/helpers/scroll-history'
import { useScrollCache } from '../hooks/useScrollCache'
import { useFilterScroll } from '../hooks/useFilterScroll'

import { ResultsCard } from './ResultCard'
import { CollectionToolbar } from './CollectionToolbar'
import { FilterDrawer } from './FilterDrawer'
import { Pagination } from './Pagination'
import { SkeletonCard } from './SkeletonCard'

import useOfflineWishlist from '@/components/hooks/useOfflineWishlist'
import { useWishlist } from '@/components/hooks/useWishlist'

const DEFAULT_PER_PAGE = 24

function SearchGrid() {
  history.scrollRestoration = 'manual'
  const { results, isLoading } = useAthosContext()
  const currentResults = useMerchedResults()
  const [firstLoad, setFirstLoad] = useState(true)
  const hasStartedLoading = useRef(false)
  const { setScrollElement } = useFilterScroll()
  const [beaconNode, setBeaconNode] = useState()

  useBeaconLogRender()
  useBeaconLogImpressions(beaconNode)
  const logRedirect = useBeaconLogRedirect()
  const query = useOption('query', { defaultValue: '', syncWithURL: false })

  const { wishlist, getKeys, ...callbacks } = useOfflineWishlist()
  const { wishlists: omneoWishlists } = useWishlist()

  const wishlistItems = useMemo(() => {
    return getKeys()
  }, [wishlist])

  useEffect(() => {
    if (isLoading) hasStartedLoading.current = true
    if (!isLoading && hasStartedLoading.current) setFirstLoad(false)
  }, [isLoading])

  useEffect(() => {
    const redirectUrl = results?.merchandising?.redirect
    if (!redirectUrl) return
    logRedirect(redirectUrl)
    window.location.href = redirectUrl
  }, [results?.merchandising?.redirect])

  useEffect(() => {
    const resultsTitle = document.querySelector('[data-search-results-title]')
    const noResultsTitle = document.querySelector('[data-search-no-results-title]')
    if (resultsTitle) {
      resultsTitle.textContent = query ? `Showing results for "${query}"` : 'Search'
    }
    if (noResultsTitle) {
      noResultsTitle.textContent = query ? `No results for "${query}"` : 'No results'
    }
  }, [query])

  useScrollCache([currentResults, firstLoad])

  const filtersOpenByDefault = useRef(typeof window !== 'undefined' && window.innerWidth >= 1024)
  const stickyFilters = GM_STATE?.plp?.collectionToolbar !== 'non-sticky'
  const showSkeleton = firstLoad && (isLoading || currentResults.length === 0)

  if (!isLoading && !firstLoad && currentResults.length === 0) {
    return <div data-no-results />
  }

  return (
    <x-modal
      className="group/filters flex w-full flex-col"
      data-id="filters"
      data-ui={filtersOpenByDefault.current ? 'visible' : undefined}
    >
      <CollectionToolbar sticky={stickyFilters} />
      <div
        data-plp-scroll-target
        ref={(el) => {
          setScrollElement(el)
          setBeaconNode(el)
        }}
        className="flex w-full md:py-10"
      >
        <FilterDrawer nested showToggle={false} stickyFilters={stickyFilters} />
        <div className="flex min-w-0 flex-1 flex-col md:px-10">
          <div className="grid h-fit grid-cols-2 gap-x-2 gap-y-6 md:grid-cols-4 md:gap-x-4 md:group-data-visible/filters:grid-cols-3">
            {showSkeleton ? (
              Array.from({ length: DEFAULT_PER_PAGE }).map((_, i) => <SkeletonCard className={'transition-all duration-300'} key={i} />)
            ) : (
              currentResults?.map((item, index) => {
                return 'value' in item ? (
                  <div
                    className="relative col-span-full aspect-square md:col-span-1"
                    key={item.value}
                  >
                    <div
                      dangerouslySetInnerHTML={{ __html: item.value }}
                      className="absolute inset-0"
                    />
                  </div>
                ) : (
                  <ResultsCard
                    wishlist={{
                      omneoWishlists,
                      offlineWishlistItems: wishlistItems,
                      wsCallbacks: callbacks,
                      ...(item.handle.includes('gift-card') && { type: 'none' })
                    }}
                    pageType="search"
                    query={query}
                    result={item}
                    index={index}
                    key={item.handle}
                    className="wk-wishlist-button-target"
                  />
                )
              })
            )}
          </div>
          <Pagination />
        </div>
      </div>
    </x-modal>
  )
}

export function WrappedSearchGrid() {
  scrollHistoryManager.updateHistoryState()
  const urlParams = new URLSearchParams(window.location.search)
  const queryParam = urlParams.get('query')

  return (
    <AthosSearchProvider query={queryParam} resultsPerPage={DEFAULT_PER_PAGE}>
      <BeaconAnalyticsProvider type="search">
        <SearchGrid />
      </BeaconAnalyticsProvider>
    </AthosSearchProvider>
  )
}
